// THE TILES IN 3D (docs SIGNOFF-SPEC S1, centre): one instanced box mesh, a colour per
// tile. Only the tiles whose colour changed since the last date are written again, so
// scrubbing through a timeline repaints a handful of boxes, not twenty thousand.
//
// A tile (i, j, k) is drawn at x = i, z = j (plan), y = k (up), one unit per tile.
// Views: 3D (orbit), Plan (from above), Section (from the side). A click picks a tile.
// pointer-space-exempt: the click is turned into the canvas's own -1..1 coordinates (clientX
// over its rendered rect, both zoomed px), never into a style.
import React, { useEffect, useRef } from "react";
import { Button, Tooltip } from "antd";
import { PlusOutlined, MinusOutlined, ExpandOutlined } from "@ant-design/icons";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { gridLinesGroup } from "./tileOverlay";

const GAP = 0.92;

export default function TileScene({ tiles, colorOf, visible, highlight, selectedKey, focusKeys, view = "3d", onSelect, height = 520, captureRef, points = null, gridLines = null }) {
  const host = useRef(null);
  const st = useRef({});

  // the scene, once per tile set
  useEffect(() => {
    const el = host.current;
    if (!el || !tiles || !tiles.tiles) return undefined;
    const list = tiles.tiles;
    const g = tiles.grid;
    const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0xf7f8fa, 1);
    el.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    scene.add(new THREE.AmbientLight(0xffffff, 0.75));
    const sun = new THREE.DirectionalLight(0xffffff, 0.75);
    sun.position.set(-0.6, 1, 0.4);
    scene.add(sun);
    const center = new THREE.Vector3(g.nx / 2, g.ny / 2, g.nz / 2);
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -10000, 10000);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.copy(center);
    controls.enableDamping = false;
    controls.screenSpacePanning = true;

    const geom = new THREE.BoxGeometry(GAP, GAP, GAP);
    const mat = new THREE.MeshLambertMaterial({ color: 0xffffff });
    const mesh = new THREE.InstancedMesh(geom, mat, Math.max(list.length, 1));
    const m = new THREE.Matrix4();
    const c = new THREE.Color("#d9dde3");
    list.forEach((t, idx) => {
      m.makeTranslation(t.i + 0.5, t.k + 0.5, t.j + 0.5);
      mesh.setMatrixAt(idx, m);
      mesh.setColorAt(idx, c);
    });
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    scene.add(mesh);

    // outlines: the selected tile, and the tiles a day's export touched
    const edges = new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1));
    const outline = new THREE.Group();
    scene.add(outline);

    const s = st.current;
    Object.assign(s, { renderer, scene, camera, controls, mesh, list, outline, edges, colors: new Array(list.length).fill(""), shown: new Array(list.length).fill(true), center, g, zoom: 1 });

    const frame = () => {
      const w = el.clientWidth || 600;
      const h = el.clientHeight || height;
      renderer.setSize(w, h, false);
      renderer.domElement.style.width = "100%";
      renderer.domElement.style.height = `${h}px`;
      const span = Math.max(g.nx, g.nz, g.ny * 1.5) * 0.75 / s.zoom;
      const aspect = w / h;
      camera.left = -span * aspect; camera.right = span * aspect; camera.top = span; camera.bottom = -span;
      camera.updateProjectionMatrix();
      renderer.render(scene, camera);
    };
    s.frame = frame;
    s.render = () => renderer.render(scene, camera);
    controls.addEventListener("change", s.render);
    const ro = new ResizeObserver(frame);
    ro.observe(el);

    // a click (not a drag) picks the tile under it
    const ray = new THREE.Raycaster();
    let down = null;
    const onDown = (e) => { down = { x: e.clientX, y: e.clientY }; };
    const onUp = (e) => {
      if (!down || Math.hypot(e.clientX - down.x, e.clientY - down.y) > 4) return;
      const r = renderer.domElement.getBoundingClientRect();
      ray.setFromCamera(new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1), camera);
      const hit = ray.intersectObject(mesh, false).find((h) => s.shown[h.instanceId]);
      if (hit && s.onSelect) s.onSelect(list[hit.instanceId]);
    };
    renderer.domElement.addEventListener("pointerdown", onDown);
    renderer.domElement.addEventListener("pointerup", onUp);
    if (captureRef) captureRef.current = () => { s.render(); return renderer.domElement.toDataURL("image/png"); };

    return () => {
      ro.disconnect();
      controls.dispose();
      renderer.domElement.removeEventListener("pointerdown", onDown);
      renderer.domElement.removeEventListener("pointerup", onUp);
      geom.dispose(); mat.dispose(); edges.dispose(); mesh.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
      st.current = {};
    };
  }, [tiles, height, captureRef]);

  useEffect(() => { st.current.onSelect = onSelect; }, [onSelect]);

  // the building grid (owner 2026-11-01), its model-frame lines turned into tile units
  useEffect(() => {
    const s = st.current;
    if (!s.scene || !s.g || !gridLines || !gridLines.length) return undefined;
    const { origin, edge } = s.g;
    const tu = (p) => [(p[0] - origin.x) / edge, 0, (p[2] - origin.z) / edge];
    const group = gridLinesGroup(gridLines.map((l) => ({ label: l.label, start: tu(l.start), end: tu(l.end) })), { origin: { x: 0, y: 0, z: 0 }, edge: 1, ny: s.g.ny });
    s.scene.add(group);
    if (s.render) s.render();
    return () => {
      if (s.scene) s.scene.remove(group);
      group.traverse((o) => { if (o.geometry) o.geometry.dispose(); if (o.material) { if (o.material.map) o.material.map.dispose(); o.material.dispose(); } });
      if (s.render) s.render();
    };
  }, [gridLines, tiles, height]);

  // the camera for the view
  useEffect(() => {
    const s = st.current;
    if (!s.camera) return;
    const { camera, controls, center, g } = s;
    const d = Math.max(g.nx, g.ny, g.nz) * 4;
    if (view === "plan") {
      camera.up.set(0, 0, -1);
      camera.position.set(center.x, center.y + d, center.z);
      controls.enableRotate = false;
    } else if (view === "section") {
      camera.up.set(0, 1, 0);
      camera.position.set(center.x, center.y, center.z + d);
      controls.enableRotate = false;
    } else {
      camera.up.set(0, 1, 0);
      camera.position.set(center.x + d * 0.6, center.y + d * 0.55, center.z + d * 0.6);
      controls.enableRotate = true;
    }
    controls.target.copy(center);
    camera.lookAt(center);
    controls.update();
    s.frame();
  }, [view, tiles]);

  // colours and visibility: only what changed is written
  useEffect(() => {
    const s = st.current;
    if (!s.mesh) return;
    const col = new THREE.Color();
    const m = new THREE.Matrix4();
    let colorDirty = false;
    let matDirty = false;
    s.list.forEach((t, idx) => {
      const hex = colorOf(t);
      if (hex !== s.colors[idx]) {
        s.mesh.setColorAt(idx, col.set(hex));
        s.colors[idx] = hex;
        colorDirty = true;
      }
      const show = visible ? visible(t) : true;
      if (show !== s.shown[idx]) {
        if (show) m.makeTranslation(t.i + 0.5, t.k + 0.5, t.j + 0.5);
        else m.makeScale(0, 0, 0);
        s.mesh.setMatrixAt(idx, m);
        s.shown[idx] = show;
        matDirty = true;
      }
    });
    if (colorDirty && s.mesh.instanceColor) s.mesh.instanceColor.needsUpdate = true;
    if (matDirty) s.mesh.instanceMatrix.needsUpdate = true;
    if (colorDirty || matDirty) s.render();
  }, [colorOf, visible, tiles]);

  // outlines
  useEffect(() => {
    const s = st.current;
    if (!s.outline) return;
    while (s.outline.children.length) {
      const o = s.outline.children.pop();
      if (o.material) o.material.dispose();
    }
    const add = (t, color) => {
      const line = new THREE.LineSegments(s.edges, new THREE.LineBasicMaterial({ color }));
      line.position.set(t.i + 0.5, t.k + 0.5, t.j + 0.5);
      s.outline.add(line);
    };
    const keys = highlight || new Set();
    if (keys.size && keys.size < 3000) s.list.forEach((t, idx) => { if (keys.has(t.key) && s.shown[idx]) add(t, 0xe8590c); });
    const sel = selectedKey && s.list.find((t) => t.key === selectedKey);
    if (sel) add(sel, 0x111827);
    s.render();
  }, [highlight, selectedKey, tiles, visible]);

  // the model inside the tiles: element centres as faint points, the tiles see-through
  useEffect(() => {
    const s = st.current;
    if (!s.scene) return undefined;
    if (!points || !points.length) {
      s.mesh.material.transparent = false; s.mesh.material.opacity = 1; s.mesh.material.depthWrite = true; s.mesh.material.needsUpdate = true;
      s.render();
      return undefined;
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(points, 3));
    const mat = new THREE.PointsMaterial({ color: 0x4b5563, size: 1.5, sizeAttenuation: false, transparent: true, opacity: 0.55, depthWrite: false });
    const cloud = new THREE.Points(geom, mat);
    s.scene.add(cloud);
    s.mesh.material.transparent = true; s.mesh.material.opacity = 0.45; s.mesh.material.depthWrite = false; s.mesh.material.needsUpdate = true;
    s.render();
    return () => {
      s.scene.remove(cloud); geom.dispose(); mat.dispose();
      if (s.mesh) { s.mesh.material.transparent = false; s.mesh.material.opacity = 1; s.mesh.material.depthWrite = true; s.mesh.material.needsUpdate = true; }
      if (s.render) s.render();
    };
  }, [points, tiles]);

  // frame the camera on given tiles (the link page frames the targets)
  useEffect(() => {
    const s = st.current;
    if (!s.camera || !focusKeys || !focusKeys.length) return;
    const pick = s.list.filter((t) => focusKeys.includes(t.key));
    if (!pick.length) return;
    const box = new THREE.Box3();
    pick.forEach((t) => box.expandByPoint(new THREE.Vector3(t.i, t.k, t.j)).expandByPoint(new THREE.Vector3(t.i + 1, t.k + 1, t.j + 1)));
    const c = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    s.controls.target.copy(c);
    const off = s.camera.position.clone().sub(s.center);
    s.camera.position.copy(c.clone().add(off));
    s.zoom = Math.max(1, Math.min(6, Math.max(s.g.nx, s.g.nz) / Math.max(size.x, size.z, 4)));
    s.controls.update();
    s.frame();
  }, [focusKeys, tiles]);

  const zoom = (f) => {
    const s = st.current;
    if (!s.frame) return;
    s.zoom = f === 0 ? 1 : Math.max(0.3, Math.min(12, s.zoom * f));
    if (f === 0) { s.controls.target.copy(s.center); s.controls.update(); }
    s.frame();
  };

  return (
    <div className="signoff-scene" style={{ position: "relative", height }}>
      <div ref={host} style={{ width: "100%", height }} data-testid="signoff-scene" />
      <div className="signoff-scene-tools">
        <Tooltip title="Zoom in" placement="left"><Button size="small" icon={<PlusOutlined />} onClick={() => zoom(1.25)} /></Tooltip>
        <Tooltip title="Zoom out" placement="left"><Button size="small" icon={<MinusOutlined />} onClick={() => zoom(0.8)} /></Tooltip>
        <Tooltip title="Fit the model" placement="left"><Button size="small" icon={<ExpandOutlined />} onClick={() => zoom(0)} /></Tooltip>
      </div>
    </div>
  );
}
